import type {CaseReducer, PayloadAction} from '@reduxjs/toolkit'
import set from 'lodash/set'
import unset from 'lodash/unset'

import type {Step} from '../../types'

import type {PipelineDraftState} from './EditPipelinePage.slices.types'
import {
  deleteIfEmpty,
  ensurePipelineDraftEntry,
  getPipelineDraftEntry,
  getJob,
} from './EditPipelinePage.slices.utils'

export const setSteps: CaseReducer<
  PipelineDraftState,
  PayloadAction<{
    id: string
    jobId: string
    steps: Step[]
    branch: string
  }>
> = (state, action) => {
  const {id, jobId, steps, branch} = action.payload
  const job = getJob(state, id, jobId, branch)
  if (job != null) {
    job.steps = steps
  }
}

export const setStepParameter: CaseReducer<
  PipelineDraftState,
  PayloadAction<{
    id: string
    jobId: string
    index: number
    name: string
    value: string | null
    branch: string
  }>
> = (state, action) => {
  const {id, jobId, index, name, value, branch} = action.payload
  const step = getJob(state, id, jobId, branch)?.steps?.[index]
  if (step != null) {
    // `name` is a JsonForms instance path (e.g. `auth-method.username-and-password.password`),
    // so write it into the nested step structure instead of as a flat dotted key.
    if (value == null) {
      unset(step, name)
    } else {
      set(step, name, value)
    }
  }
}

export const deleteStep: CaseReducer<
  PipelineDraftState,
  PayloadAction<{
    id: string
    jobId: string
    index: number
    branch: string
  }>
> = (state, action) => {
  const {id, jobId, index, branch} = action.payload
  const pipeline = ensurePipelineDraftEntry(state, id, branch)
  pipeline.deleted ??= {}
  pipeline.deleted!.steps ??= {}
  pipeline.deleted!.steps![jobId] ??= []
  pipeline.deleted!.steps![jobId]!.push(index)
}

export const restoreStep: CaseReducer<
  PipelineDraftState,
  PayloadAction<{
    id: string
    jobId: string
    index: number
    branch: string
  }>
> = (state, action) => {
  const {id, jobId, index, branch} = action.payload
  const deleted = getPipelineDraftEntry(state, id, branch)?.deleted?.steps
  if (deleted?.[jobId]?.includes(index)) {
    deleted[jobId] = deleted[jobId].filter(i => i !== index)
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    deleteIfEmpty(deleted, jobId) &&
      deleteIfEmpty(getPipelineDraftEntry(state, id, branch)!.deleted, 'steps') &&
      deleteIfEmpty(getPipelineDraftEntry(state, id, branch), 'deleted')
  }
}

export const reorderSteps: CaseReducer<
  PipelineDraftState,
  PayloadAction<{id: string; jobId: string; order: number[]; branch: string}>
> = (state, action) => {
  const {id, jobId, order, branch} = action.payload
  const pipeline = ensurePipelineDraftEntry(state, id, branch)
  pipeline.reordered ??= {}
  pipeline.reordered!.steps ??= {}
  pipeline.reordered!.steps![jobId] = order
}
